import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useAsyncEffect from 'use-async-effect';
import { FiMessageCircle, FiUser, FiSearch } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { GetUsersResult, UserItem } from '../../../model/GetUsersResult';
import { InvestorQuestion } from '../../../model/CommunicationsQuestionsResult';
import { ReplyToComment, V2 } from '../../../model/Roles';
import api from '../../../api/api';
import { to } from '../../../util/util';
import ContactsModal from './contacts/ContactsModal';
import Dropdown from '../Dropdown';
import Toggle from '../Toggle';
import { toast } from '../Toast';
import { initials } from '../Header';

export interface TeamMember {
    email: string;
    roles: string[]; // stock roles on this client
    isV2: boolean;
    you: boolean;
}

const roleLabel = (r: string) => r.replace(/([a-z])([A-Z])/g, '$1 $2');
const moderators = (s: string | null | undefined) => (s || '').split(/[,;\s]+/).map(x => x.trim().toLowerCase()).filter(Boolean);

// the people with a role on this client, from GET /api/users (needs Edit Users). Without it only "you" is listed.
export const useStockTeam = (stockId: string | undefined) => {
    const me = useSelector((state: IGlobalState) => state.user);
    const [users, setUsers] = useState<UserItem[] | null>(null);
    const [limited, setLimited] = useState(false);

    useAsyncEffect(async isMounted => {
        if (!stockId) return;
        const [err, data] = await to(api().getUsers());
        if (!isMounted()) return;
        if (err) { setUsers([]); setLimited(true); return; }
        setUsers((data as GetUsersResult).users);
        setLimited(false);
    }, [stockId]);

    const team = useMemo<TeamMember[]>(() => {
        if (!users || !stockId) return [];
        const mine = me?.username?.toLowerCase();
        const list = users.filter(u => u.stockRoles[stockId]?.length || u.username.toLowerCase() === mine).map(u => ({
            email: u.username,
            roles: u.stockRoles[stockId] ?? [],
            isV2: u.roles.includes(V2),
            you: u.username.toLowerCase() === mine,
        }));
        if (me && !list.some(x => x.you)) list.push({ email: me.username, roles: me.stockRoles?.[stockId] ?? [], isV2: V2 in (me.roles ?? {}), you: true });
        // you first, then whoever can answer questions, then the rest
        const rank = (m: TeamMember) => m.you ? 0 : m.roles.includes(ReplyToComment) ? 1 : 2;
        return list.sort((a, b) => rank(a) - rank(b) || a.email.localeCompare(b.email));
    }, [users, stockId, me]);

    return { team, loaded: users !== null, limited };
};

const memberLabel = (m: TeamMember) => `${m.email}${m.you ? ' (you)' : ''}`;
const memberSub = (m: TeamMember) => m.roles.length ? m.roles.map(roleLabel).join(' · ') : 'No role on this client';

interface SettingsProps {
    onClose: () => void;
}

// spec 288:11026 (note 454:16772). Pre-filled from the stock's moderator emails; Save is preview only.
export const QuestionSettingsModal: React.FC<SettingsProps> = ({ onClose }) => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const me = useSelector((state: IGlobalState) => state.user);
    const { team, limited } = useStockTeam(stock?.id);
    const emails = moderators(stock?.moderatorEmail);
    // moderator emails get each new question; links open v2 when the first moderator has the V2 role (decisions 2026-10-12)
    const first = team.find(m => m.email.toLowerCase() === emails[0]);
    const [notify, setNotify] = useState(!!me?.username && emails.includes(me.username.toLowerCase()));
    const [autoPublish, setAutoPublish] = useState(false); // answers are only published with Answer & Publish
    const [assignee, setAssignee] = useState('');
    const [approver, setApprover] = useState('');

    const options = [{ value: '', label: 'Not connected yet' }, ...team.map(m => ({ value: m.email, label: memberLabel(m) }))];
    const save = () => {
        onClose();
        toast.info('Preview only: question settings aren’t connected yet, so nothing was saved');
    };

    const notConnected = (title: string, sub: string) => <div className="iq-set-row">
        <div className="iq-set-t"><strong>{title}</strong><span>{sub}</span></div>
        <span className="pill">Not connected yet</span>
    </div>;

    return <ContactsModal icon={<FiMessageCircle />} title="Investor Question Settings" sub="How questions arrive and who answers them" onClose={onClose}
        buttons={<button type="button" className="btn-primary" onClick={save}>Save Settings</button>}>
        <div className="iq-modal">
            {notConnected('Allow questions on the Website Hub', 'Shows the “Ask the company” box')}
            {notConnected('Only signed‑in investors can ask', 'Reduces spam and lets you see their holding')}
            <div className="iq-set-row">
                <div className="iq-set-t">
                    <strong>Notify me by email for each new question</strong>
                    <span>{emails.length ? `Moderator emails: ${emails.join(', ')}` : 'No moderator emails set'}
                        {first && ` · links open ${first.isV2 ? 'this inbox' : 'the v1 reply page'}`}</span>
                </div>
                <Toggle checked={notify} onChange={setNotify} label="Notify me by email for each new question" />
            </div>
            {notConnected('Remind after 48 hours', 'Overdue questions are tagged 48h+ in the list')}
            <div className="iq-set-row">
                <div className="iq-set-t">
                    <strong>Publish answers automatically</strong>
                    <span>{autoPublish ? 'On — every answer is published' : 'Off — answers are published only when you choose'}</span>
                </div>
                <Toggle checked={autoPublish} onChange={setAutoPublish} label="Publish answers automatically" />
            </div>
            <div className="iq-field">
                <span>Questions are assigned to</span>
                <Dropdown value={assignee} options={options} onChange={setAssignee} searchable={options.length > 8} />
            </div>
            <div className="iq-field">
                <span>Answers must be approved by</span>
                <Dropdown value={approver} options={options} onChange={setApprover} searchable={options.length > 8} />
            </div>
            {limited && <p className="iq-hint">Listing the team needs the Edit Users role, so only you are shown.</p>}
        </div>
    </ContactsModal>;
};

interface AssignProps {
    question: InvestorQuestion;
    onClose: () => void;
}

// spec 423:15068. No assignment model yet: lists the client's team (real), Assign is preview only.
export const AssignQuestionModal: React.FC<AssignProps> = ({ question, onClose }) => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const { team, loaded, limited } = useStockTeam(stock?.id);
    const [query, setQuery] = useState('');
    const [picked, setPicked] = useState('');
    const [email, setEmail] = useState(true);
    const [note, setNote] = useState('');

    const q = query.trim().toLowerCase();
    const shown = team.filter(m => !q || `${m.email} ${m.roles.map(roleLabel).join(' ')}`.toLowerCase().includes(q));
    const assign = () => {
        onClose();
        toast.info(`Preview only: assigning isn’t connected yet, so ${picked} wasn’t assigned${email ? ' or emailed' : ''}`);
    };

    return <ContactsModal icon={<FiUser />} title="Assign This Question" sub={`“${question.comment}”`} onClose={onClose}
        buttons={<button type="button" className="btn-primary" disabled={!picked} onClick={assign}>Assign</button>}>
        <div className="iq-modal">
            <label className="iq-search"><FiSearch /><input placeholder="Search your team" value={query} onChange={e => setQuery(e.target.value)} /></label>
            {!loaded && <p className="iq-hint">Loading your team…</p>}
            <div className="iq-people">
                {shown.map(m => <button type="button" key={m.email} className={`iq-person${picked === m.email ? ' on' : ''}`} role="radio" aria-checked={picked === m.email} onClick={() => setPicked(m.email)}>
                    <span className="iq-av">{initials(m.email.split('@')[0])}</span>
                    <span className="iq-person-t"><strong>{memberLabel(m)}</strong><span>{memberSub(m)}</span></span>
                    <span className="iq-radio" />
                </button>)}
                {loaded && shown.length === 0 && <p className="iq-hint">{q ? 'No one matches your search.' : 'No one has a role on this client.'}</p>}
            </div>
            {limited && <p className="iq-hint">Listing the team needs the Edit Users role, so only you are shown.</p>}
            <div className="iq-set-row">
                <div className="iq-set-t"><strong>Email them about it</strong><span>Includes the question and a link to answer</span></div>
                <Toggle checked={email} onChange={setEmail} label="Email them about it" />
            </div>
            <label className="iq-field">
                <span>Note <em>(optional)</em></span>
                <textarea rows={2} value={note} onChange={e => setNote(e.target.value)} />
            </label>
        </div>
    </ContactsModal>;
};
